import { useState } from 'react';

import { HourglassBottom } from '@mui/icons-material';
import { Alert, Box, Paper, Theme, useMediaQuery } from '@mui/material';
import { MRT_PaginationState, MRT_SortingState } from 'material-react-table';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import CardDemonstration from 'src/components/CardDemonstration/CardDemonstration';
import MetaTags from 'src/components/Seo/MetaTags';
import TabTemplate from 'src/components/UI/TabTemplate';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  fetchTimelineData,
  getTimelineFilters,
} from 'src/services/convocations';
import {
  getInformativeTextByLocation,
  getPayWallBanner,
} from 'src/services/informative';
import { ScrollToTop } from 'src/utils/scroll';

import { TimelineFilters } from './Filters';
import { TimelineTable } from './Table';

// Tipos para os filtros
export interface FilterState {
  operacao: string;
  mercado: string;
  tipoAssembleia: string;
  favoritos: boolean;
  especiedocumento: string;
  categoriadocumento: string;
}

export default function Timeline() {
  const { auth } = useAuthContext();
  const hasPermission =
    !!auth?.user?.subscription?.plan?.permissions?.linha_do_tempo;

  const { pathname, search } = useLocation();

  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: 'datareferencia',
      desc: true,
    },
  ]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const isExtraSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const [timelineFilters, setTimelineFilters] = useState<FilterState>({
    operacao: '',
    mercado: '',
    tipoAssembleia: '',
    favoritos: false,
    especiedocumento: '',
    categoriadocumento: '',
  });

  const { data: description } = useQuery('timelineDescription', () =>
    getInformativeTextByLocation('timeline'),
  );

  const { data: contentPayWallBanner } = useQuery(
    'paywall',
    () => getPayWallBanner('timeline'),
    {
      enabled: !auth.isLoading,
    },
  );

  const { data: filtersData, isLoading: isLoadingFilters } = useQuery({
    queryKey: ['filtersData'],
    queryFn: () => getTimelineFilters(),
  });

  const {
    data: { data = [], meta } = {},
    isError,
    isLoading: isLoadingTimeline,
  } = useQuery({
    queryKey: ['timeline-data', timelineFilters, sorting, pagination],
    queryFn: () => {
      return fetchTimelineData({
        operation: timelineFilters.operacao,
        market: timelineFilters.mercado,
        favorites: timelineFilters.favoritos,
        type: timelineFilters.tipoAssembleia,
        category: timelineFilters.categoriadocumento,
        species: timelineFilters.especiedocumento,
        sort: sorting,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
      });
    },
    enabled: !!filtersData,
  });

  const handleFiltersChange = (newFilters: FilterState) => {
    setTimelineFilters(newFilters);
  };

  const isLoading = isLoadingFilters || isLoadingTimeline;

  return (
    <ScrollToTop>
      <MetaTags
        title="Linha do Tempo"
        description="Acompanhe os principais eventos de todas as operações do mercado financeiro."
      />
      <TabTemplate
        title="Linha do Tempo"
        subtitle={description?.data?.timeline?.informationText ?? ''}
        breadcrumbs1="Linha do Tempo"
        titleIcon={<HourglassBottom />}
        tabContent={[]}
        tabIndex={0}
        handleChange={() => {}}
      />
      <Box component="section" position={!hasPermission ? 'relative' : 'unset'}>
        <Paper
          elevation={2}
          sx={{
            p: 2,
            borderRadius: '5px',
            minHeight: isExtraSmall && !hasPermission ? 420 : '100%',
          }}>
          {!hasPermission ? (
            <CardDemonstration
              title={contentPayWallBanner?.timeline?.title}
              subTitle={contentPayWallBanner?.timeline?.subTitle}
              labelButton={
                auth.isLoggedIn
                  ? contentPayWallBanner?.timeline?.labelButton
                  : 'Acessar'
              }
              url={
                auth.isLoggedIn
                  ? contentPayWallBanner?.timeline?.url
                  : `/login?redirectUrl=${pathname}${search}`
              }
            />
          ) : (
            <Box
              p={1}
              borderRadius={'5px'}
              display="flex"
              flexDirection="column"
              gap={2}>
              <TimelineFilters
                filtersData={
                  filtersData ?? {
                    types: [],
                    categories: [],
                    species: [],
                    markets: [],
                  }
                }
                filters={timelineFilters}
                onFiltersChange={handleFiltersChange}
              />
              <TimelineTable
                timelineFilters={timelineFilters}
                sorting={sorting}
                pagination={pagination}
                setPagination={setPagination}
                setSorting={setSorting}
                data={data}
                meta={meta ?? { pagination: { total: 0 } }}
                isError={isError}
                isLoading={isLoading}
              />
            </Box>
          )}
          <Alert severity="info" sx={{ mx: 1 }}>
            <b>Aviso</b>: Estes resumos foram gerados por IA e podem não
            refletir todos os detalhes dos documentos originais. Recomenda-se
            consultar o documento completo para informações precisas.
          </Alert>
        </Paper>
      </Box>
    </ScrollToTop>
  );
}
